import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';

import {
  classifySeedStatus,
  getBulkEligibilityNote,
} from '@/components/actions';
import { AppRouteBody } from '@/components/AppRouteBody';
import { Entitled } from '@/components/Entitled';
import { createSeedExportConfig } from '@/components/export/configs/seed.export.config';
import {
  buildActiveFilters,
  type SeedKey,
  UniversalExportModal,
} from '@/components/export/universal';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { NoteModal } from '@/components/modals';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { Spinner } from '@/components/ui/spinner';
import { useGetSeedFilters } from '@/hooks/api/useGetSeedFilters';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { CredentialModals } from '@/sections/asset/components/tabs/credentials/CredentialModals';
import { useCredentialManager } from '@/sections/asset/components/tabs/credentials/useCredentialManager';
import { CellFiltersProvider } from '@/sections/seeds/components/CellFiltersContext';
import { ImportSeedsModal } from '@/sections/seeds/components/modals/ImportSeedsModal';
import { SeedRemoveTag } from '@/sections/seeds/components/modals/SeedRemoveTag';
import { SeedTag } from '@/sections/seeds/components/modals/SeedTag';
import { SeedsActions } from '@/sections/seeds/components/SeedsActions';
import { SeedsDiscoveryPanel } from '@/sections/seeds/components/SeedsDiscoveryPanel';
import { SeedsFilters } from '@/sections/seeds/components/SeedsFilters';
import { SeedsHeader } from '@/sections/seeds/components/SeedsHeader';
import { SeedsModals } from '@/sections/seeds/components/SeedsModals';
import { SeedsTable } from '@/sections/seeds/components/SeedsTable';
import {
  useSeedData,
  useSeedDefaultStatusFilter,
  useSeedFilters,
  useSeedsActions,
  useSeedSearch,
} from '@/sections/seeds/hooks';
import { useSeedCommentHandlers } from '@/sections/seeds/hooks/actions';
import { useEffectiveSeedFilter } from '@/sections/seeds/hooks/useEffectiveSeedFilter';
import { SeedTabValue, SeedType } from '@/sections/seeds/types';
import { inferTabFromInput } from '@/sections/seeds/utils/inferTabFromInput';
import { seedUrlParams } from '@/sections/seeds/utils/seedUrlParams';
import { BulkActivityAnnouncer } from '@/sections/topNavBar/BulkJobsDrawer/BulkActivityAnnouncer';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Seed, SeedStatus } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { collapseRegistrarFilterValues } from '@/utils/formatRegistrar.util';

/**
 * Maps SeedStatus to a human-readable bulk action name for the confirmation dialog.
 * Defined outside the component to avoid recreation on every render.
 */
function getBulkActionName(status: SeedStatus): string {
  switch (status) {
    case SeedStatus.Freeze:
      return 'freeze';
    case SeedStatus.Delete:
      return 'delete';
    case SeedStatus.Accept:
    case SeedStatus.AcceptLow:
      return 'approve';
    case SeedStatus.Reject:
      return 'reject';
    default:
      return 'update';
  }
}

export const Seeds = () => {
  const queryClient = useQueryClient();
  const seedExportConfig = useMemo(() => createSeedExportConfig(), []);

  // Use centralized feature lock for web applications tab
  const {
    locked: isWebApplicationsLocked,
    lockTitle: webAppsLockTitle,
    lockDescription: webAppsLockDescription,
    featureName: webAppsFeatureName,
  } = useFeatureLock('seeds.webApplications');

  const { canWriteAssets } = useEntitlements();

  // Praetorian-ness gates the top-level, tenant-wide Discovery tab (ENG-5078);
  // `userIdFromUrl` is the currently-viewed tenant email (impersonated tenant
  // or self) that the tenant-wide Pius panel operates on.
  const { isPraetorianUser, userIdFromUrl: tenantEmail } = useAuth();

  // Tab-strip selection is a SUPERSET of SeedType: it also carries the
  // Praetorian-only `discovery` tab, which is a navigation surface, not a seed
  // entity type. `seedUrlParams.read()` only ever returns a real SeedType (it
  // whitelists domain/ip/webapplication/preseed), so `discovery` is transient
  // state entered by clicking the tab — never deep-linked.
  const [tabValue, setTabValue] = useState<SeedTabValue>(() => {
    const urlFilters = seedUrlParams.read();
    return urlFilters.type || 'domain';
  });

  // Coerce the tab value to a real SeedType for every seed-entity concern
  // (data, filters, table, modals, export). `discovery` is a navigation-only
  // value, not a SeedType, so those hooks never receive it; the discovery panel
  // is rendered by an EARLY branch below instead of the table, so this coerced
  // `type` is inert while the Discovery tab is active.
  //
  // While Discovery is active we HOLD the last real (non-discovery) SeedType
  // steady rather than snapping to `domain`. useSeedFilters(type) has a
  // debounced effect that writes `{type}` back to the URL, so coercing to
  // `domain` would rewrite e.g. `?type=ip` → `?type=domain` and a reload/back
  // would land the user on the wrong tab. Preserving the prior real type keeps
  // the URL reflecting where the user actually was.
  //
  // The last real type is tracked in state, adjusted during render (React's
  // "storing information from previous renders" pattern — a ref read during
  // render or a setState-in-effect are both barred by the React Compiler lint).
  // On a real tab this converges in one extra render and `type` reads `tabValue`
  // directly, so nothing renders with a stale value; when Discovery is active no
  // update fires and `type` falls back to the last real type. `discovery` is
  // never deep-linked, so the initial value is always a real type.
  const [lastSeedType, setLastSeedType] = useState<SeedType>(
    tabValue === 'discovery' ? 'domain' : tabValue
  );
  if (tabValue !== 'discovery' && tabValue !== lastSeedType) {
    setLastSeedType(tabValue);
  }
  const type: SeedType = tabValue === 'discovery' ? lastSeedType : tabValue;

  // Filter state management with hybrid persistence (localStorage + URL)
  const {
    filters: filter,
    setFilters: setFilter,
    setSort,
    resetFilters,
  } = useSeedFilters(type);

  // Search with debouncing for header SearchBar
  const { searchTerm, setSearchTerm } = useSeedSearch(
    filter.search || '',
    debouncedTerm => {
      setFilter(prev => ({
        ...prev,
        search: debouncedTerm,
      }));
    }
  );

  const [seedForComment, setSeedForComment] = useState<Seed | null>(null);

  const { data: seedFilterOptions, status: filterOptionsStatus } =
    useGetSeedFilters();

  // Reconcile a persisted registrar filter (legacy "name [Tag = X]" values or a
  // frozen expanded raw set) against the CURRENT registrar options so the IN-query
  // — and the activeQuery used by select-all-matching + export — matches both
  // legacy and newly-normalized rows. Stored state is intentionally untouched.
  // Fallback while options load = pass stored values through (today's behavior).
  const effectiveFilter = useEffectiveSeedFilter(
    filter,
    seedFilterOptions,
    filterOptionsStatus
  );

  // Coordinated data fetching (seeds + counts + preseed count + totalCount)
  const {
    seeds: apiData,
    error,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    hasNextPage,
    isAllEmpty,
    isFilteredEmpty,
    refetchSeeds,
    isAccountEmpty,
    totalCount: totalDatabaseCount = 0,
    activeQuery,
  } = useSeedData(effectiveFilter);

  // Apply default "Approved" status filter per tab, once per session (ENG-4982)
  useSeedDefaultStatusFilter({
    seedType: type,
    filters: filter,
    setFilters: setFilter,
    isFilteredEmpty,
    dataStatus: status,
    isFetching,
  });

  const { sortedOptions: seedSortedOptions } = useEntityFilters('Seed');
  const seedFieldLabels = useMemo(
    () => Object.fromEntries(seedSortedOptions.map(o => [o.value, o.label])),
    [seedSortedOptions]
  );

  const { modal } = useGlobalState();

  // Selection state management — Gmail-style two-step bulk selection
  const bulkSelection = useBulkSelection({
    data: apiData || [],
    activeFilters: (activeQuery ?? {}) as import('@/types').GraphQuery,
    filterVersion: filter,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection: isSeedsSelected,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount: bulkSelectedCount,
    state: selectionState,
  } = bulkSelection;

  const exportSelectedSeedKeys = isSelectAll
    ? []
    : (selectedKeys as unknown as readonly SeedKey[]);

  // Derive selectedSeeds from selectedKeys for action handlers
  const selectedSeeds = (apiData || []).filter(s =>
    selectedKeys.includes(s.key)
  );

  // F6 fix: only show Credentials in Manage dropdown when exactly 1 web app seed is selected
  const showCredentials =
    type === 'webapplication' && selectedSeeds.length === 1;
  const credentialManager = useCredentialManager({
    assetKey: showCredentials ? selectedSeeds[0].key : '',
    // Seeds don't carry a `secret` field — default credential is not applicable
    assetSecret: undefined,
    group: showCredentials ? selectedSeeds[0].group : undefined,
    identifier: showCredentials ? selectedSeeds[0].identifier : undefined,
  });

  useEffect(() => {
    if (!showCredentials) {
      credentialManager.onClose();
    }
  }, [showCredentials]);

  // Invalidation function for seed queries
  const invalidateSeeds = () => {
    queryClient.invalidateQueries({
      predicate: query => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === 'MY' && queryKey[1] === 'seed';
      },
      refetchType: 'active',
    });
  };

  // Bulk confirmation hook for the status_change bulk endpoint.
  // Encapsulates: dialog state + useBulkAction mutation + clearSelection wiring.
  // NOTE: does NOT call queryClient.invalidateQueries — that is handled by
  // useActiveBulkJobs (polls job status and invalidates on terminal state).
  const statusConfirm = useBulkConfirmation({
    resource: 'seed',
    action: 'status_change',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the untag bulk endpoint.
  // Same shape as statusConfirm — uses resource:'seed', action:'untag'.
  // Wire format locked: action='untag', params:{tag}.
  const untagConfirm = useBulkConfirmation({
    resource: 'seed',
    action: 'untag',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the NEW hard-delete ("Delete Permanently") flow.
  // Distinct from the soft bulk Delete: soft goes through `statusConfirm`
  // (action:'status_change', params:{status:'D'}); hard delete dispatches a
  // separate action:'delete' bulk job. The two never share a code path, so the
  // soft Delete → 'delete' actionName mapping in getBulkActionName is untouched.
  const deleteConfirm = useBulkConfirmation({
    resource: 'seed',
    action: 'delete',
    bulkSelection,
    clearSelection,
  });

  // Bulk hard delete is Praetorian-gated (button hidden in SeedsActions) and
  // accepts an explicit selection only, capped at 1000 keys.
  const openHardDeleteConfirmation = () => {
    deleteConfirm.openConfirmation({
      action: 'delete',
      actionName: 'delete permanently',
      params: {},
    });
  };

  // Coordinator hook - composes modal state + action handlers
  const { modalState, modalHandlers, handlers } = useSeedsActions({
    selectedSeeds,
    selectedRowsCount: selectedKeys.length,
    invalidateSeeds,
    setConfirmStatus: () => {},
    selectionState,
    onBulkRequested: (status: SeedStatus) => {
      statusConfirm.openConfirmation({
        action: 'status_change',
        actionName: getBulkActionName(status),
        params: { status },
      });
    },
  });

  // Comment handler hook for direct access to handleAddComment
  const { handleAddComment } = useSeedCommentHandlers();

  const handleComment = async (comment: string) => {
    // Use seedForComment if set (single row comment), otherwise use selectedSeeds (bulk comment)
    const seedsToComment = seedForComment ? [seedForComment] : selectedSeeds;

    if (seedsToComment.length === 0) {
      console.warn('[Seeds] handleComment called with no seeds selected');
      return;
    }

    // Directly call handleAddComment with the correct seeds
    // Note: seedType param is not used (prefixed with _) - function uses type guard internally
    const seedType = type === 'preseed' ? 'domain' : type; // TypeScript requires non-preseed type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleAddComment(seedsToComment as any, comment, seedType);
    invalidateSeeds();
    clearSelection();
    setSeedForComment(null);
  };

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    apiData?.length || 0
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filter.advancedFilters,
        sort: filter.sort,
        type: filter.type,
      }),
    [filter.advancedFilters, filter.sort, filter.type]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Build export active filters using shared utility (reverse-maps values to labels)
  const exportActiveFilters = useMemo(() => {
    // Stored registrar values are the EXPANDED raw set, but buildActiveFilters'
    // findOptionLabel matches option.value (the collapsed label) — so collapse
    // registrar back to its label here or chips show duplicate raw values while
    // the dropdown shows one. All other filter keys pass through untouched.
    const registrar = filter.advancedFilters?.registrar;
    const chipFilters = registrar
      ? {
          ...filter.advancedFilters,
          registrar: collapseRegistrarFilterValues(registrar),
        }
      : filter.advancedFilters;
    return buildActiveFilters(
      chipFilters as Record<string, unknown> | undefined,
      (seedFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
        {},
      filter.search,
      seedFieldLabels
    );
  }, [
    filter.search,
    filter.advancedFilters,
    seedFilterOptions,
    seedFieldLabels,
  ]);

  // Detect initial loading state to prevent table UI flicker
  // Matches the pattern used by Vulnerabilities and Technologies tables
  // Include filterOptionsStatus so the spinner stays until BOTH data AND filter options are ready
  const isInitialLoading =
    status === 'pending' || filterOptionsStatus === 'pending';

  // Whether to render the tenant-wide Pius Discovery panel INSTEAD of the seeds
  // table (ENG-5078). Defense-in-depth gating (three layers):
  //   1. the tab only exists for Praetorians (`useSeedTabs` in config/tabs.tsx),
  //   2. this render gate re-checks `isPraetorianUser` on the SAME derived
  //      signal — no reset `useEffect` — so a non-Praetorian who lands on a
  //      stale `discovery` selection sees NO discovery content and falls back
  //      to the domain table (the tab strip omits `discovery`, so
  //      `getSelectedTab` resolves to the first tab). This mirrors the section's
  //      derived-value-not-effect rule that dodges the auth-boot race where
  //      `isPraetorianUser` is briefly `false` (see the Delete-filter note in
  //      seeds/CLAUDE.md),
  //   3. `CustomerDiscoveryTab` self-guards on `userType === 'praetorian'`.
  // Kept inside the `isAccountEmpty === false` gate (below) for the same reason
  // the tab strip is: an empty account shows no tabs, so Discovery is
  // unreachable until the tenant has seeds.
  const showDiscoveryPanel =
    tabValue === 'discovery' && isPraetorianUser && isAccountEmpty === false;

  // Keyboard action handler
  function handleKeyboardAction(code: string, data: Seed[]) {
    if (!canWriteAssets) return;
    const keyMap = {
      KeyA: SeedStatus.Accept,
      KeyR: SeedStatus.Reject,
      KeyD: SeedStatus.Delete,
      KeyF: SeedStatus.Freeze,
    };
    const status = keyMap[code as keyof typeof keyMap];
    if (status) {
      modalState.confirmation.open(status, data);
    }
  }

  // Confirmation modal handler
  const handleConfirmStatusChange = async () => {
    if (!modalState.confirmation.data) return;
    const { status } = modalState.confirmation.data;

    // Use the appropriate status handler from coordinator
    switch (status) {
      case SeedStatus.Accept:
        await handlers.handleApprove();
        break;
      case SeedStatus.Reject:
        await handlers.handleReject();
        break;
      case SeedStatus.Freeze:
        await handlers.handleFreeze();
        break;
      case SeedStatus.Delete:
        await handlers.handleDelete();
        break;
    }

    clearSelection();
    modalHandlers.closeConfirmationModal();
  };

  return (
    <AppRouteBody title="Seeds">
      {isInitialLoading ? (
        <Spinner centered size="xl" />
      ) : (
        <>
          {/* Conditionally render tabs only when account has seeds */}
          {isAccountEmpty === false && (
            <div className="mb-4">
              <SeedsHeader activeTab={tabValue} onTabChange={setTabValue} />
            </div>
          )}

          {/* Content area with optional lock overlay */}
          <div className="relative flex flex-1 flex-col overflow-hidden">
            {showDiscoveryPanel ? (
              // Top-level, tenant-wide Discovery surface (ENG-5078): renders the
              // Pius panel INSTEAD of the table — no filters/search/selection/
              // bulk/export. `tenantEmail` is guarded for truthiness before
              // mounting (a defensive no-op render otherwise; an authed user
              // always resolves a tenant email).
              tenantEmail ? (
                <SeedsDiscoveryPanel tenantEmail={tenantEmail} />
              ) : null
            ) : (
              <FeatureLock
                locked={type === 'webapplication' && isWebApplicationsLocked}
                title={webAppsLockTitle}
                description={webAppsLockDescription}
                action={createContactSupportButton(webAppsFeatureName)}
              >
                {/* Unified Controls Container: Filters + Selection Bar */}
                {!isAllEmpty && (
                  <TableControlsContainer
                    data-testid="seeds-controls"
                    filterLabel={true}
                    filters={
                      <SeedsFilters
                        filters={filter}
                        setFilters={setFilter}
                        seedType={type}
                      />
                    }
                    filtersRight={
                      <SearchBar
                        size="sm"
                        className="h-fit w-64"
                        placeholder="Search seeds"
                        value={searchTerm}
                        onChange={setSearchTerm}
                      />
                    }
                    selectionBar={
                      <>
                        <TableSelectionBar
                          embedded={true}
                          alwaysVisible={true}
                          isVisible={isSeedsSelected}
                          selectedCount={bulkSelectedCount}
                          totalCount={apiData?.length || 0}
                          hasNextPage={hasNextPage}
                          isSelectAll={isSelectAll}
                          totalDatabaseLabel={`${totalDatabaseCount.toLocaleString()} approved ${type}s`}
                          resourceLabel={`${type}s`}
                          emptySelectionMessage={`Select ${type}s to perform bulk actions`}
                          actions={
                            <Entitled entitlement="write_assets">
                              <SeedsActions
                                seedType={type}
                                selectedSeeds={selectedSeeds}
                                selectedRowsCount={bulkSelectedCount}
                                modalHandlers={modalHandlers}
                                actionHandlers={handlers}
                                onClearSelection={clearSelection}
                                onCredentialsClick={
                                  showCredentials
                                    ? credentialManager.open
                                    : undefined
                                }
                                onHardDelete={openHardDeleteConfirmation}
                                isSelectAll={selectionState.mode === 'all'}
                              />
                            </Entitled>
                          }
                        />
                        <SelectionBanner
                          selectedCount={bulkSelectedCount}
                          totalLoadedCount={apiData?.length || 0}
                          hasNextPage={hasNextPage ?? false}
                          isSelectAll={isSelectAll}
                          isAllLoadedSelected={isAllLoadedSelected}
                          resourceLabel={`${type}s`}
                          onSelectAllMatching={selectAllMatching}
                          onClearSelection={clearSelection}
                          enableSelectAllMatching={activeQuery != null}
                        />
                      </>
                    }
                  />
                )}

                {/* Single table-scoped aria-live region for bulk action status.
                  UX divergence #6: one region per table, NOT one per row. */}
                <BulkActivityAnnouncer />
                {/* Unified table - works for ALL types including preseed.
                  CellFiltersProvider exposes the live filter state to config-
                  rendered cells (e.g. OriginCell) for filter-aware ordering. */}
                <CellFiltersProvider value={filter}>
                  <SeedsTable
                    key={`${type}-${combinedTableKey}`}
                    seedType={type}
                    data={apiData}
                    status={status}
                    error={error}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    sort={
                      filter.sort && {
                        orderBy: filter.sort.orderBy,
                        descending: filter.sort.descending,
                        onSort: setSort,
                      }
                    }
                    selectedRows={selectedKeys}
                    onSelectionChange={setSelectedKeys}
                    onKeyboardAction={handleKeyboardAction}
                    onWebCrawl={seedKey => {
                      modal.selectUserAgent.onAssetKeysChange([seedKey]);
                    }}
                    onCommentClick={
                      canWriteAssets
                        ? seed => {
                            setSeedForComment(seed);
                            modalHandlers.openCommentModal();
                          }
                        : undefined
                    }
                    isAllEmpty={isAllEmpty}
                    onResetFilters={resetFilters}
                    onAddSeed={
                      type !== 'preseed'
                        ? modalHandlers.openAddSeedModal
                        : undefined
                    }
                  />
                </CellFiltersProvider>
              </FeatureLock>
            )}
          </div>
        </>
      )}
      <SeedsModals
        modalState={modalState}
        seedType={type}
        seedForComment={seedForComment}
        onConfirmStatusChange={handleConfirmStatusChange}
        onRefetchSeeds={(seedTypeOption, textInput) => {
          if (seedTypeOption && textInput) {
            setTabValue(inferTabFromInput(seedTypeOption, textInput));
          } else {
            refetchSeeds();
          }
        }}
      />
      <NoteModal
        open={modalState.comment.isOpen}
        onClose={() => {
          setSeedForComment(null);
          modalHandlers.closeCommentModal();
        }}
        onSubmit={handleComment}
        resourceType="Seed"
        mode="simple"
        selectedCount={seedForComment ? 1 : bulkSelectedCount}
        presentation="modal"
      />
      <SeedTag
        open={modalState.tag.isOpen}
        onClose={modalHandlers.closeTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
      />
      <SeedRemoveTag
        open={modalState.removeTag.isOpen}
        onClose={modalHandlers.closeRemoveTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
        selectionState={selectionState}
        onBulkRequested={tag =>
          untagConfirm.openConfirmation({
            action: 'untag',
            actionName: 'untag',
            params: { tag },
          })
        }
      />
      <UniversalExportModal
        config={seedExportConfig}
        open={modalState.export.isOpen}
        onClose={modalHandlers.closeExportModal}
        selectedRows={exportSelectedSeedKeys}
        onClearSelection={() => {
          clearSelection();
          modalHandlers.closeExportModal();
        }}
        activeFilters={exportActiveFilters}
        activeQuery={activeQuery}
      />
      <ImportSeedsModal
        open={modalState.import.isOpen}
        onClose={modalHandlers.closeImportModal}
        onImport={() => {
          refetchSeeds();
          modalHandlers.closeImportModal();
        }}
      />
      <BulkConfirmationDialog
        isOpen={statusConfirm.confirmPayload !== null}
        actionName={statusConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="seeds"
        isSelectAll={selectionState.mode === 'all'}
        itemCount={bulkSelectedCount}
        isPending={statusConfirm.isPending}
        eligibilityNote={
          selectionState.mode === 'all' || !statusConfirm.confirmPayload
            ? undefined
            : (getBulkEligibilityNote(
                selectedSeeds.map(s => s.status),
                statusConfirm.confirmPayload.params.status as string,
                classifySeedStatus,
                bulkSelectedCount
              ) ?? undefined)
        }
        onCancel={statusConfirm.closeConfirmation}
        onConfirm={statusConfirm.submitConfirmed}
      />
      <BulkConfirmationDialog
        isOpen={untagConfirm.confirmPayload !== null}
        actionName={untagConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="seeds"
        isSelectAll={selectionState.mode === 'all'}
        itemCount={bulkSelectedCount}
        isPending={untagConfirm.isPending}
        onCancel={untagConfirm.closeConfirmation}
        onConfirm={untagConfirm.submitConfirmed}
      />
      <BulkConfirmationDialog
        isOpen={deleteConfirm.confirmPayload !== null}
        actionName={deleteConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="seeds"
        isSelectAll={selectionState.mode === 'all'}
        itemCount={bulkSelectedCount}
        isPending={deleteConfirm.isPending}
        isHardDelete
        hardDeleteHint="Active seeds may be re-discovered and re-added by future scans. To remove without permanent deletion, use Delete instead."
        onCancel={deleteConfirm.closeConfirmation}
        onConfirm={deleteConfirm.submitConfirmed}
      />
      {/* Seeds don't carry burp_type — web app seeds are always web applications, never web services */}
      <CredentialModals manager={credentialManager} isWebService={false} />
    </AppRouteBody>
  );
};

// Re-export types needed by other components
export type { SeedFilter } from '@/hooks/useSeeds';
